/**
 * Single source of truth for conversation channels: normalizes the varied raw
 * "type"/"channel" values from backends into a canonical key, label, and icon.
 */

export type ChannelKey = 'voice' | 'sms' | 'chat' | 'email' | 'default';

// Note: voice reads as "Call".
export const CHANNEL_LABELS: Record<ChannelKey, string> = {
  voice: 'Call',
  sms: 'SMS',
  chat: 'Chat',
  email: 'Email',
  default: 'Unknown',
};

const CHANNEL_ICONS: Record<ChannelKey, string> = {
  voice: 'pi pi-phone',
  sms: 'pi pi-envelope',
  chat: 'pi pi-comments',
  email: 'pi pi-envelope',
  default: 'pi pi-question-circle',
};

export function normalizeChannel(value: string | null | undefined): ChannelKey {
  const v = (value ?? '').toString().toLowerCase().trim();
  if (!v) return 'default';
  if (v.includes('voice') || v.includes('call')) return 'voice';
  if (v.includes('sms') || v.includes('text')) return 'sms';
  if (v.includes('chat') || v.includes('message')) return 'chat';
  if (v.includes('email') || v.includes('mail')) return 'email';
  return 'default';
}

// Recognized channels use their canonical label; unrecognized values are title-cased, empty is "Unknown".
export function getChannelLabel(value: string | null | undefined): string {
  const key = normalizeChannel(value);
  if (key !== 'default') return CHANNEL_LABELS[key];

  const raw = (value ?? '').toString().trim();
  if (!raw) return CHANNEL_LABELS.default;

  return raw
    .toLowerCase()
    .split(/[\s_-]+/)
    .map((part) => (part ? `${part[0].toUpperCase()}${part.slice(1)}` : ''))
    .join(' ');
}

export function getChannelIcon(value: string | null | undefined): string {
  return CHANNEL_ICONS[normalizeChannel(value)];
}
